import React, { useState, useEffect } from 'react';

const TrafficForm = ({ onAddCampaign, onSaveEdit, onCancelEdit, editingCampaign }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    channel: 'facebook',
    investment: '',
    leads: '',
    sales: '',
    revenue: ''
  });

  const [dateError, setDateError] = useState('');

  useEffect(() => {
    if (editingCampaign) {
      setFormData({
        date: editingCampaign.date || new Date().toISOString().split('T')[0],
        channel: editingCampaign.channel || 'facebook',
        investment: editingCampaign.investment || '',
        leads: editingCampaign.leads || '',
        sales: editingCampaign.sales || '',
        revenue: editingCampaign.revenue || ''
      });
    } else {
      setFormData({
        date: new Date().toISOString().split('T')[0],
        channel: 'facebook',
        investment: '',
        leads: '',
        sales: '',
        revenue: ''
      });
    }
  }, [editingCampaign]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Validação de data ao alterar o campo
    if (name === 'date') {
      validateDate(value);
    }
  };

  const validateDate = (value) => {
    // Aceita 'yyyy-mm-dd' ou 'dd/mm/yyyy'
    const isoPattern = /^\d{4}-\d{2}-\d{2}$/;
    const brPattern = /^\d{2}\/\d{2}\/\d{4}$/;

    if (!isoPattern.test(value) && !brPattern.test(value)) {
      setDateError('Formato de data inválido. Use yyyy-mm-dd ou dd/mm/yyyy');
    } else {
      setDateError('');
    }
  };

  const formatDate = (dateString) => {
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  const handleSalesChange = (e) => {
    const sales = parseInt(e.target.value) || 0;
    const averageTicket = 100;
    const estimatedRevenue = sales * averageTicket;

    setFormData(prev => ({
      ...prev,
      sales: e.target.value,
      revenue: estimatedRevenue.toString()
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (dateError) {
      alert('Corrija o campo de data antes de enviar.');
      return;
    }

    if (!formData.date || !formData.channel || !formData.investment) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    const formattedData = {
      ...formData,
      date: formatDate(formData.date),
      investment: parseFloat(formData.investment) || 0,
      leads: parseInt(formData.leads) || 0,
      sales: parseInt(formData.sales) || 0,
      revenue: parseFloat(formData.revenue) || 0
    };

    if (editingCampaign) {
      onSaveEdit(formattedData);
    } else {
      onAddCampaign(formattedData);
    }

    setFormData({
      date: new Date().toISOString().split('T')[0],
      channel: 'facebook',
      investment: '',
      leads: '',
      sales: '',
      revenue: ''
    });
  };

  const calculateCPA = () => {
    const investment = parseFloat(formData.investment) || 0;
    const sales = parseInt(formData.sales) || 0;
    if (sales === 0) return 'N/A';
    return (investment / sales).toFixed(2);
  };

  const calculateROI = () => {
    const investment = parseFloat(formData.investment) || 0;
    const revenue = parseFloat(formData.revenue) || 0;
    if (investment === 0) return 'N/A';
    return (((revenue - investment) / investment) * 100).toFixed(2);
  };

  return (
    <form className="traffic-form" onSubmit={handleSubmit}>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="date">Data</label>
          <input
            type="text"
            id="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            placeholder="aaaa-mm-dd ou dd/mm/aaaa"
            className={dateError ? 'border border-red-500' : ''}
            required
          />
          {dateError && <p className="text-red-500 text-sm mt-1">{dateError}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="channel">Canal</label>
          <select
            id="channel"
            name="channel"
            value={formData.channel}
            onChange={handleChange}
            required
          >
            <option value="facebook">Facebook</option>
            <option value="tiktok">TikTok</option>
            <option value="google">Google</option>
            <option value="taboola">Taboola</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="investment">Investimento (R$)</label>
          <input
            type="number"
            id="investment"
            name="investment"
            value={formData.investment}
            onChange={handleChange}
            placeholder="0.00"
            step="0.01"
            min="0"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="leads">Leads/Cadastros</label>
          <input
            type="number"
            id="leads"
            name="leads"
            value={formData.leads}
            onChange={handleChange}
            placeholder="0"
            min="0"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="sales">Vendas</label>
          <input
            type="number"
            id="sales"
            name="sales"
            value={formData.sales}
            onChange={handleSalesChange}
            placeholder="0"
            min="0"
          />
        </div>

        <div className="form-group">
          <label htmlFor="revenue">Receita (R$)</label>
          <input
            type="number"
            id="revenue"
            name="revenue"
            value={formData.revenue}
            onChange={handleChange}
            placeholder="0.00"
            step="0.01"
            min="0"
          />
        </div>
      </div>

      <div className="form-metrics">
        <div className="metric-calc">
          <strong>CPA:</strong> R$ {calculateCPA()}
        </div>
        <div className="metric-calc">
          <strong>ROI:</strong> {calculateROI()}%
        </div>
      </div>

      <div className="form-actions">
        {editingCampaign ? (
          <>
            <button type="submit" className="btn-primary">Salvar Alterações</button>
            <button type="button" className="btn-secondary" onClick={onCancelEdit}>Cancelar</button>
          </>
        ) : (
          <button type="submit" className="btn-primary">Adicionar Campanha</button>
        )}
      </div>
    </form>
  );
};

export default TrafficForm;
